/* import __COLOCATED_TEMPLATE__ from './background-form.hbs'; */
import { computed } from '@ember/object';
import Component from '@ember/component';
import { A } from '@ember/array';
import { handleFormErrorOrModal } from 'sh-www/utils/handles-errors';
import {
  emailValidator,
  fullNameValidator,
  phoneValidator,
  websiteValidator
} from '../../utils/validators';

/**
 * @param {Ember.A} availableCountries
 * @param {string} crCitizenships
 * @returns {Ember.A}
 */
const getCrCitizenships = (availableCountries, crCitizenships) => {
  const countriesMap = availableCountries.reduce((accum, country) => {
    accum[country.get('name')] = country;
    return accum;
  }, { });

  return crCitizenships.split(';').reduce((accum, countryName) => {
    if (countryName) {
      accum.push(countriesMap[countryName]);
    }
    return accum;
  }, A([]));
};

const FORM_INPUTS = [
  'description',
  'personal_website',
  'equipment',
  'tags',
  'languages',
  'conflict_reporting',
  'cr_hostile_env_training',
  'cr_first_aid_training',
  'cr_citizenships',
  'cr_full_name',
  'cr_relationship',
  'cr_phone',
  'cr_email',
];

const FORM_TUPLES = [
  ['description', 'descriptionValidator'],
  ['personal_website', 'websiteValidator'],
  ['cr_full_name', 'crFullNameValidator'],
  ['cr_relationship', 'crRelationshipValidator'],
  ['cr_phone', 'crPhoneValidator'],
  ['cr_email', 'crEmailValidator'],
];

const HOSTILE_ENV_PROPS = [
  'cr_full_name',
  'cr_relationship',
  'cr_phone',
  'cr_email',
];

/** @module */
const SettingsBackgroundForm = Component.extend({
  /** @inheritDoc */
  tagName: '',

  /** @type {Ember.A} */
  availableEquipment: A([]),

  /** @type {Ember.A} */
  availableLanguages: A([]),

  /** @type {Ember.A} */
  availableCountries: A([]),

  /** @type {string} */
  personal_website: '',

  /** @type {string} */
  description: '',

  /** @type {Ember.A} */
  equipment: undefined,

  /** @type {Ember.A} */
  languages: undefined,

  /** @type {Ember.A} */
  tags: undefined,

  /** @type {boolean} */
  conflict_reporting: false,

  /** @type {boolean} */
  cr_hostile_env_training: false,

  /** @type {boolean} */
  cr_first_aid_training: false,

  /** @type {Ember.A} */
  cr_citizenships: undefined,

  /** @type {string} */
  cr_full_name: '',

  /** @type {string} */
  cr_relationship: '',

  /** @type {string} */
  cr_phone: '',

  /** @type {string} */
  cr_email: '',

  /** @type {boolean} */
  submitting: false,

  /** @type {Ember.ComputedProperty} */
  descriptionValidator: computed('description', function() {
    return undefined;
  }),

  /** @type {Ember.ComputedProperty} */
  websiteValidator: computed('personal_website', function() {
    const website = this.get('personal_website');
    if (website) return websiteValidator(website);
  }),

  /** @type {Ember.ComputedProperty} */
  crFullNameValidator: computed('cr_full_name', 'conflict_reporting', function() {
    return this._emergencyContactValidator('cr_full_name',  fullNameValidator);
  }),

  /** @type {Ember.ComputedProperty} */
  crRelationshipValidator: computed('cr_relationship', 'conflict_reporting', function() {
    return this._emergencyContactValidator('cr_relationship', () => undefined);
  }),

  /** @type {Ember.ComputedProperty} */
  crPhoneValidator: computed('cr_phone', 'conflict_reporting', function() {
    return this._emergencyContactValidator('cr_phone', phoneValidator);
  }),

  /** @type {Ember.ComputedProperty} */
  crEmailValidator: computed('cr_email', 'conflict_reporting', function() {
    return this._emergencyContactValidator('cr_email', emailValidator);
  }),

  /** @inheritDoc */
  init() {
    this._super(...arguments);

    const {
      user,
      profile,
      availableCountries,
    } = this.getProperties([
      'user',
      'profile',
      'availableCountries',
    ]);

    const props = {
      errors: { },
      description: user.get('description') || '',
      tags: user.get('tags').map(tag => tag.get('name') || ''),
      languages: profile.get('languages').toArray(),
      equipment: profile.get('equipment').toArray(),
      personal_website: profile.get('personalWebsite') || '',
      conflict_reporting: profile.get('conflictReporting') || false,
      cr_citizenships: getCrCitizenships(availableCountries, profile.get('crCitizenships') || ''),
      cr_hostile_env_training: profile.get('crHostileEnvTraining') || false,
      cr_first_aid_training: profile.get('crFirstAidTraining') || false,
      cr_full_name: profile.get('crFullName') || '',
      cr_relationship: profile.get('crRelationship') || '',
      cr_phone: profile.get('crPhone') || '',
      cr_email: profile.get('crEmail') || '',
    };

    this.setProperties(props);
  },

  /** @inheritDoc */
  actions: {
    toggleConflictReporting() {
      if (this.toggleProperty('conflict_reporting')) {
        return;
      }

      this.setProperties(HOSTILE_ENV_PROPS.reduce((accum, prop) => {
        accum[`errors.${prop}`] = undefined;
        return accum;
      }, { }));
    },

    /**
     * @param {string} prop
     */
    toggleProperty(prop) {
      this.toggleProperty(prop);
    },

    /**
     * @param {string} field
     * @param {string} value
     */
    updateField(field, value) {
      this.setProperties({ [field]: value, [`errors.${field}`]: undefined });
    },

    /**
     * @param {string} field
     * @param {string} validator
     */
    validateField(field, validator) {
      const error = this.get(validator);

      if (error) {
        this.set(`errors.${field}`, error);
      }
    },

    /**
     * @param {string} prop
     * @param {any} value
     */
    addChip(prop, value) {
      this.get(prop).pushObject(value);
    },

    /**
     * @param {string} prop
     * @param {any} value
     */
    removeChip(prop, value) {
      this.get(prop).removeObject(value);
    },

    /**
     *
     */
    handleSubmit() {
      if (this.get('submitting') || !this._validateForm()) {
        return;
      }

      this.set('submitting', true);

      this.get('updateUser')(this.getProperties(FORM_INPUTS)).catch((e) => {
        handleFormErrorOrModal(
          this,
          e,
          FORM_INPUTS,
          false,
          'There was an error updating your account. Please refresh and try again.',
          'settingsBackgroundFail -- error updating user profile.' + JSON.stringify(e)
        );
      }).finally(() => {
        this.set('submitting', false);
      });
    },
  },

  /**
   * @returns {boolean}
   * @private
   */
  _validateForm() {
    const [errors, hasError] = FORM_TUPLES.reduce(([errors, hasError], [key, validator]) => {
      const err = this.get(validator);

      if (err) {
        errors[key] = err;
        hasError = true;
      }

      return [errors, hasError];
    }, [{ }, false]);

    this.set('errors', errors);
    return !hasError;
  },

  /**
   * @param {string|undefined} field
   * @param {function(x: string): string|undefined} f
   * @returns string|undefined
   */
  _emergencyContactValidator(field, f) {
    const conflictReporting = this.get('conflict_reporting');
    const prop = this.get(field);

    if (!conflictReporting) return;
    if (!prop) return 'This is a required field';

    return f(prop);
  },
});

export default SettingsBackgroundForm;
